import { Translator } from '@miq/utils';

export interface QuarterSelectorValue {
  periodValue: number;
  periodYear: number;
}

export const enum QuarterSelectorType {
  Monthly = 'monthly',
  Quarter = 'quarterly',
  Yearly = 'yearly',
  Annual = 'annual',
}

const updatePeriodValue = (
  periodType: QuarterSelectorType,
  periodYear: number,
  periodQtrMon: number,
  maxYear: number,
  today: Date
) => {
  if (periodType === QuarterSelectorType.Quarter) {
    // if periodQtrMon is not null, it means that the value is the from month else from year
    if (periodQtrMon) {
      return Math.ceil(periodQtrMon / 3);
    }
    return periodYear === maxYear ? Math.ceil((today.getMonth() + 1) / 3) : 4;
  }
  if (periodType === QuarterSelectorType.Monthly) {
    // if periodQtrMon is null, it means that the value is the from quarter  else from year
    if (periodQtrMon) {
      const curMonth = today.getMonth() + 1;
      // if the periodQtrMon * 3 is greater than the current month, then return the current month
      return periodYear === maxYear && periodQtrMon * 3 > curMonth ? curMonth : periodQtrMon * 3;
    }
    return periodYear === maxYear ? today.getMonth() + 1 : 12;
  }
  return null;
};

const updatePeriodYear = (periodType: QuarterSelectorType, periodYear: number, maxYear: number) => {
  if (periodType === QuarterSelectorType.Yearly && periodYear > maxYear) {
    return maxYear;
  }
  return periodYear;
};

export const updateValues = (
  val: Record<string, any>,
  periodType: QuarterSelectorType,
  maxYear: number,
  today: Date,
  changedFromYear: boolean
) => {
  if (val) {
    val.periodYear = updatePeriodYear(periodType, val.periodYear, maxYear);
    val.periodValue = updatePeriodValue(
      periodType,
      val.periodYear,
      changedFromYear ? null : val.periodValue,
      maxYear,
      today
    );
  }
};

export enum ErrorCode {
  ReportingServerTimeout = 0,
  ExportIDPending = 101,
  ExportDailyLimitReached = 102,
  BEServerTimeout = 104,
  UnknownIssue = 500,
  BrowserTimeout = 504,
}

export const errorTypes = {
  [ErrorCode.ReportingServerTimeout]: 'recordsSearch.timeoutError', // pdf server timeout
  [ErrorCode.ExportIDPending]: 'recordsSearch.exportIdPending',
  [ErrorCode.ExportDailyLimitReached]: 'recordsSearch.exportDailyLimit',
  [ErrorCode.BEServerTimeout]: 'recordsSearch.timeoutError', // BE server timeout
  [ErrorCode.UnknownIssue]: 'recordsSearch.unknownIssue',
  [ErrorCode.BrowserTimeout]: 'recordsSearch.timeoutError', // explorer timeout / FE
};

export const handleDownloadError = (error: any, entity: string, translationService: Translator) => {
  // Convert error to string if it's not already a string
  const errorString = typeof error === 'string' ? error : String(error || '');

  if (errorString?.indexOf('Export/Download limit') > 0) {
    const extractedData = errorString.split(/[^0-9]/).filter(data => data !== '');
    const userLimit = extractedData[3];
    const exceedRecords = extractedData[5];
    const downloadError =
      translationService.translate('recordsSearch.exportDownloadLimitOf') +
      ' ' +
      `${userLimit}` +
      ' ' +
      translationService.translate('shared.entityPlurals.' + entity) +
      translationService.translate('recordsSearch.per24HourPeriod') +
      translationService.translate('recordsSearch.lastestRequestRecord') +
      `${exceedRecords}` +
      translationService.translate('recordsSearch.overYourLimitAndAdjust');
    return { isDownloadLimitError: true, message: downloadError };
  } else {
    return { isDownloadLimitError: false, message: 'recordsSearch.errWhileExportingRecords' };
  }
};
